export default `---
slug: llm-finetuning
title: "My First Dance with LLMs"
year: 2024
category: personal
featured: true
tags: [Python, Mistral 7B, LoRA, Quantization, Scrapy, Fine-tuning, LangChain]
thumbnail: /images/projects/20_Syllog.jpg
description: "Fine-tuning Mistral 7B with Q-LoRA and 4-bit quantization, with web-scraped data and ChatGPT-generated Q&A pairs. The result was bad. The learning was priceless."
relatedLinks:
  - label: "Salesforce Prompt Builder"
    href: /projects/salesforce-prompt-builder
---
![llm finetuning](/images/projects/20_Syllog.jpg)

Winter 2024 found me in the Netherlands, fresh off completing the [Apziva bootcamp](https://www.apziva.com/) and hungry for more. I'd just wrapped up an intensive learning experience, but instead of taking a breather, I was itching to dive into something new. *(Classic me, honestly)*

That's when serendipity struck. My dear friend **Edoardo** *The Usual* had just launched his startup [Syllog](https://syllog.ai/en/) together with Davide and Fabrizio. These guys were absolute machines — coding all day, all night, completely immersed in building their AI startup. I knew them well, and honestly, they're some of the smartest people I know. When they mentioned wanting to explore some new technology on the side, I couldn't help myself: "Let's do something together!"

Of course, being winter 2024, we were right at the **start of the AI boom**. *(Perfect timing, right?)* The question that fascinated us was deceptively simple: **How easy was it to fine-tune an LLM?**

## The Experiment

This was the era of exciting open-source breakthroughs. [Llama 2](https://www.llama.com/llama2/) had dropped a few months before, and the European [Mistral 7B](https://www.mistral.ai/) had just come out. We wanted to understand how complex it really was to take one of these models, fine-tune it, and see what happened.

Since the Syllog team was *(understandably)* super busy, the research fell to me.

**The data challenge:** Finding good quality data is *hard*. I turned to web scraping — first time I'd ever done it. I used [Scrapy](https://www.scrapy.org/) to gather content, then dove deep into NLP processing to clean it up. Then I needed to transform the raw content into question-answer pairs for chatbot training. Enter [ChatGPT API](https://openai.com/api/) — I fed it the content and asked it to generate Q&A pairs. *Synthetic data generation, pretty cool stuff!*

**The fine-tuning:** We paid for Google Colab credits, thinking we were all set. **Plot twist:** it wasn't enough. Seven billion parameters can't fit on a single consumer setup. So we got creative:

- 4-bit **quantization** to reduce memory footprint
- [LoRA (Low-Rank Adaptation)](https://arxiv.org/abs/2106.09685) for parameter-efficient fine-tuning

**The result:** Not so good, honestly. *(But incredibly formative!)*

## What I Learned

The technical part of fine-tuning isn't actually the bottleneck. With Python libraries, it's literally a few lines of code. The **real** challenges are:

- **Data creation and quality** — This is huge. Getting clean, relevant, properly formatted data is where most of the work happens.
- **Pipeline monitoring** — Keeping track of training progress, catching issues early, understanding what's happening during the process.
- **Resource management** — Balancing model size, memory constraints, and computational requirements.

Our model's output was... let's call it "bad." *(I'm being generous here)* But that made me incredibly thankful for all the brilliant people who do the heavy lifting on commercial LLMs. The difference in quality was staggering.

[GitHub Repo](https://github.com/Ilvecho/LLM_fine_tuning) — built in Python on Google Colab, with Scrapy for content gathering, LangChain + ChatGPT API for synthetic data generation, and Q-LoRA with 4-bit quantization for fine-tuning.

*Shout out to Edoardo, Davide, and Fabri at Syllog for letting me tag along on this adventure, and to my dear friend Claude who was always there to help debug and brainstorm.*
`;
